import { useState, useEffect, useRef } from "react";
import type { MouseEvent, ChangeEvent } from "react";

import {
  ArrowsRepeat,
  TrashCan,
  CircleQuestion
} from "@unified-checkout/design-system/icons/index";
import { Avatar, Button, PopperTooltip } from "@unified-checkout/design-system";
import type { IImageUploaderAvatar } from "libs/design-system/src/types/Avatar";

import type { IImageUploader } from '../../../types/shared/image-uploader';
import styles from "./image-uploader.module.scss";


const LOCALIZED_TEXTS = {
  en: {
    replace: "Replace",
    delete: "Delete",
    maxSize: "(max. size: {{size}} MB)",
    uploadFailedTryAgain: "Upload failed, please try again",
    notSupportedFormat: "The file format is not supported",
    fileSize: "The file uploaded exceeds {{size}} MB",
    noFileUploaded: "Please upload a file",
    lessThanMin: "Image dimensions should be at least {{minWidth}}x{{minHeight}}px",
    moreThanMax: "Image dimensions should not exceed {{maxWidth}}x{{maxHeight}}px",
  },
  ar: {
    replace: "استبدال",
    delete: "حذف",
    maxSize: "(الحجم الأقصى: {{size}} ميغابايت)",
    uploadFailedTryAgain: "فشل الرفع، يرجى المحاولة مرة أخرى",
    notSupportedFormat: "تنسيق الملف غير مدعوم",
    fileSize: "حجم الملف المرفوع يتجاوز {{size}} ميغابايت",
    noFileUploaded: "يرجى رفع ملف",
    lessThanMin: "أبعاد الصورة يجب أن تكون على الأقل {{minWidth}}x{{minHeight}} بكسل",
    moreThanMax: "أبعاد الصورة يجب ألا تتجاوز {{maxWidth}}x{{maxHeight}} بكسل",
  }
};

const DEFAULT_FORMATS = [".png", ".jpeg", ".jpg"];

const MIN_WIDTH = 16;
const MIN_HEIGHT = 16;
const MAX_WIDTH = 2000;
const MAX_HEIGHT = 2000;

const TO_BYTE_FACTOR = 1024 * 1024;


export const ImageUploader = ({
  lang = 'en',
  size = "md",
  label,
  hint,
  showReplaceBtn,
  error,
  imgUrl,
  imgName,
  disabled = false,
  allowedFormats = DEFAULT_FORMATS,
  allowedSize = 5, // in MBs,
  minWidth = MIN_WIDTH,
  minHeight = MIN_HEIGHT,
  maxWidth = MAX_WIDTH,
  maxHeight = MAX_HEIGHT,
  onClickHandler,
  onDeleteHandler,
  onFileSelect,
  onError,
  localization
}: IImageUploader) => {

  const fileInputRef = useRef<HTMLInputElement>(null);

  const languageTexts = LOCALIZED_TEXTS[lang];
  const localizedTexts = { ...languageTexts, ...localization };

  const [imgSrc, setImgSrc] = useState(imgUrl);
  const [errMsg, setErrMsg] = useState(error);

  useEffect(() => {
    setImgSrc(imgUrl);
  }, [imgUrl]);

  useEffect(() => {
    setErrMsg(error);
  }, [error]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {

    const FILE = ((e?.target?.files && e?.target?.files[0]));
    const MAX_SIZE: any = allowedSize && (allowedSize * TO_BYTE_FACTOR);
    const fileExtension = FILE?.name.split('.').pop()?.toLowerCase() || '';
    const fileType = FILE?.type.split('/')[1];

    const fileFormats = [
      fileType,
      fileExtension,
      `.${fileExtension}`,
    ].filter(Boolean);

    const isAllowed = fileFormats.some((format) =>
      allowedFormats?.includes(format as string)
    );

    if (!FILE) return;

    if (FILE.size > MAX_SIZE) {
      setErrMsg(localizedTexts.fileSize.replace('{{size}}', String(allowedSize)));
      return;
    };

    if (!isAllowed) {
      setErrMsg(localizedTexts.notSupportedFormat);
      return;
    };

    const url = URL.createObjectURL(FILE);
    const img = new Image();
    img.src = url;

    img.onload = () => {
      const { width, height } = img;
      if (
        minWidth && (width < minWidth) ||
        minHeight && (height < minHeight)
      ) {
        setErrMsg(localizedTexts.lessThanMin
          .replace('{{minWidth}}', String(minWidth))
          .replace('{{minHeight}}', String(minHeight)));
      }
      else if (
        maxWidth && (width >= maxWidth) ||
        maxHeight && (height >= maxHeight)
      ) {
        setErrMsg(localizedTexts.moreThanMax
          .replace('{{maxWidth}}', String(maxWidth))
          .replace('{{maxHeight}}', String(maxHeight)));
      } else {
        setErrMsg('');
        onFileSelect?.({ e, file: FILE, url });
        setImgSrc(url);
      }
    };

    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    onClickHandler?.(e);
    setErrMsg('');
    fileInputRef.current?.click();
  };

  const handleDeleteClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    onDeleteHandler?.(e);
    setImgSrc(undefined);
    setErrMsg('');

    // Reset input value when deleting
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };


  const avatarProps = {
    lang,
    size,
    error: errMsg,
    imgUrl: imgSrc,
    imgName,
    disabled,
    allowedFormats,
    allowedSize,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    onClickHandler: (e) => { onClickHandler?.(e); setErrMsg(''); },
    onFileSelect,
    onError,
    localization
  } as IImageUploaderAvatar;

  return (
    <div className={styles['image-uploader-wrapper']}>
      <div className={styles['image-uploader-wrapper__info-box']}>
        {
          label &&
          <div className={styles['image-uploader-wrapper__info-box__label']}>
            {label}

            {
              hint &&
              <PopperTooltip
                hintText={hint}
              >
                {CircleQuestion}
              </PopperTooltip>
            }
          </div>
        }
        {
          imgSrc &&
          <div className={styles['image-uploader-wrapper__info-box__actions']}>
            {
              showReplaceBtn &&
              <Button
                size="sm"
                color="primary"
                variant="link"
                leadingIcon={ArrowsRepeat}
                text={localizedTexts.replace}
                disabled={disabled}
                onClickHandler={handleClick}
              />
            }
            {
              onDeleteHandler &&
              <Button
                size="sm"
                color="secondary"
                variant="link"
                leadingIcon={TrashCan}
                text={localizedTexts.delete}
                disabled={disabled}
                onClickHandler={handleDeleteClick}
              />
            }
          </div>
        }
        {
          errMsg &&
          <div className={styles['image-uploader-wrapper__info-box__error']}>
            {errMsg}
          </div>
        }
      </div>

      <Avatar
        type="image-uploader"
        inputRef={fileInputRef}
        handleFileChange={handleFileChange}
        {...avatarProps}
      />
    </div>
  )
}
